import {
  type BuildListParamsInput,
  buildListParams,
  type ListParams,
} from "./build-list-params";
import { codecForType } from "./codecs";
import { resolveOperator } from "./operators";
import type { FilterField, FilterFieldsConfig } from "./types";

export interface OptionCountParamsInput {
  fields: FilterFieldsConfig;
  /** The field whose option is counted. */
  field: FilterField<string, any, any>;
  /** The route's raw `filters` (operator brackets included) and free-text search. */
  search: Pick<
    NonNullable<BuildListParamsInput["search"]>,
    "filters" | "search"
  >;
  /** The option being counted. */
  value: string;
}

/**
 * PURE — the list params that count one option of a filter: the list's current
 * filters and search, with the field's own filter replaced by that option alone
 * (a facet count), so an option reads what ticking only it would list. No
 * `sort`: order never changes a count, and leaving it out lets sorts share the
 * cached counts.
 *
 * The option goes through the field's own `toApiFilter` (or its codec), so a
 * field with a special wire form counts the same rows its filter lists.
 */
export function optionCountParams({
  fields,
  field,
  search,
  value,
}: OptionCountParamsInput): ListParams {
  const { [field.key]: _ownFilter, ...otherFilters } = (search.filters ??
    {}) as Record<string, unknown>;
  const base = buildListParams({
    fields,
    search: { filters: otherFilters, search: search.search },
  });

  const optionValue = [value];
  const operator = resolveOperator(field, undefined, optionValue);
  const fragment = field.toApiFilter
    ? field.toApiFilter(optionValue, operator)
    : codecForType[field.type].defaultToApiFilter(
        optionValue,
        field.key,
        operator,
      );

  return { ...base, filters: { ...base.filters, ...fragment } };
}
